/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Mon Aug 05 2024
 **/

import { Column, Icon, Row, Typography, TypographyTooltip } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { Configure } from "@/frameworks/libraries/utilities";
// import { UserListType } from "../PhoneDetail";
import { Avatar } from "@/frameworks/core/components";
import styled from "@emotion/styled";
import { usePhoneCall, usePhoneRecent } from "@/frameworks/core/hooks";
import { useMemo } from "react";
import { usePersistActions } from "@/frameworks/core/persistence";

export default function GroupCallHistoryMediaLayout(props: PushServices.Call.PhoneDetailProps) {
	const phoneCall = usePhoneCall();
	const { users } = usePersistActions();
	const [userList, setUserList] = phoneCall.state.callees;
	const userListWithoutMe = userList?.filter((user) => user.id !== Configure.userID) || [];
	const phoneRecent = usePhoneRecent();
	const callHistory = phoneRecent?.list?.content.find((call) => call.id === props.callID);
	const caller = useMemo(() => users.get_user(props.callerID) ?? {}, [props.callerID]);

	return (
		<Row gap={12} horizonAlign="center">
			<Maybe
				test={userListWithoutMe.length > 3}
				fallback={
					userListWithoutMe &&
					Object.values(userListWithoutMe)?.map((user) => {
						const userCallStatus = callHistory?.callUsers?.find(
							(history) => history.id === user.userID
						)?.callUserStatus;
						return (
							<Column key={user.userID} horizonAlign="center" gap={16} style={{ width: "88px" }}>
								{userCallStatus !== "REJECTED" ? (
									<Avatar
										id={user.userID ?? ""}
										kind={"people_80"}
										key={user.userID}
										showMiniProfile={false}
									/>
								) : (
									<NoResponseUserAvatar userID={user.userID} />
								)}
								<TypographyTooltip.div
									tooltipProps={{
										hasIcon: false
									}}
									componentProps={{
										typoSize: "Body3_MD",
										ellipsis: true,
										maxLine: 1,
										stretch: true,
										align: "center"
									}}
								>
									{user.name}
								</TypographyTooltip.div>
							</Column>
						);
					})
				}
			>
				<Row gap={32}>
					<Column horizonAlign="center" gap={16}>
						<Avatar id={caller.userID ?? props.callerID} kind="people_80" />
						<Typography typoSize="Body3_MD">{caller.name}</Typography>
					</Column>
					<Column horizonAlign="center" gap={16}>
						<IconWrapper verticalAlign="center" horizonAlign="center">
							<Icon name="People24" size={28} fill="white" />
						</IconWrapper>
						<Typography typoSize="Body3_MD">-</Typography>
					</Column>
				</Row>
			</Maybe>
		</Row>
	);
}

function NoResponseUserAvatar({ userID }: { userID: string }) {
	return (
		<div style={{ position: "relative", height: "88px" }}>
			<Avatar
				id={userID ?? ""}
				kind={"people_80"}
				key={userID}
				showMiniProfile={false}
				style={{ opacity: 0.4 }}
			/>
			<NoResponseIconWrapper horizonAlign="center" verticalAlign="center">
				<Icon name="Close24" size={20} fill="white" />
			</NoResponseIconWrapper>
		</div>
	);
}

const IconWrapper = styled(Row)`
	width: 88px;
	height: 88px;
	border-radius: 100%;
	background-color: ${({ theme }) => theme.colors.accent_40_100};
`;

const NoResponseIconWrapper = styled(Row)`
	position: relative;
	bottom: 24px;
	right: -63px;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background-color: ${({ theme }) => theme.colors.warning_50_100};
`;
